"use client";
import React, { useEffect, useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  FormHelperText,
} from "@mui/material";
import { Poppins } from "next/font/google";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CTextField } from "../CustomStyles";

const poppins = Poppins({
  weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  subsets: ["latin"],
  display: "swap",
});

const PasswordField = ({ placeholder, value, onChange, error, helperText, setIsStrongPassword }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState(value || "");
  const [validationError, setValidationError] = useState({
    length: false,
    number: false,
    alphabet: false,
  });

  useEffect(() => {
    if(typeof setIsStrongPassword === 'function'){
      setIsStrongPassword((!validationError?.length && !validationError?.number && !validationError?.alphabet))
    }
  }, [validationError])

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);

  const validatePassword = (password) => {
    const lengthError = password.length < 8 || password.length > 30;
    const numberError = !/\d/.test(password);
    const alphabetError = !/[a-zA-Z]/.test(password);
    setValidationError({
      length: lengthError,
      number: numberError,
      alphabet: alphabetError,
    });
  };

  const handleChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    onChange(newPassword);
    validatePassword(newPassword);
  };

  const isPasswordValid = () =>
    !validationError.length && !validationError.number;

  return (
    <Box>
      <CTextField
        placeholder={placeholder}
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={handleChange}
        error={error || !isPasswordValid()}
        fullWidth
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                {showPassword ? <EyeOffLine /> : <OutlinedEye />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {/* Display validation conditions */}
      {/* <Box mt={1}> */}
      {(validationError.length ||
        validationError.number ||
        validationError.alphabet) &&
        password.length >= 1 && (
          <>
            <FormHelperText
              sx={{
                marginRight: 2,
                display: "block",
                // color: validationError.length ? "#BA1A1A" : "#5C677D",
                color: error ? "#BA1A1A" : "#5C677D",
                fontFamily: poppins.style.fontFamily,
                "& .text": {
                  textDecoration: validationError.length
                    ? "none"
                    : "line-through",
                },
                "& .dot": {
                  color: error
                    ? "#BA1A1A"
                    : validationError.length
                    ? "#FF6600"
                    : "#979DAC",
                },
                // "& .dot": {
                //   color: validationError.length
                //     ? "#BA1A1A"
                //     : validationError.length
                //     ? "#FF6600"
                //     : "#979DAC",
                // },
              }}
            >
              <span className="dot">●</span>&nbsp;
              <span className="text">Minimum 8 characters</span>
            </FormHelperText>

            <FormHelperText
              sx={{
                marginRight: 2,
                display: "inline",
                // color: validationError.number ? "#BA1A1A" : "#5C677D",
                fontFamily: poppins.style.fontFamily,
                color: error ? "#BA1A1A" : "#5C677D",
                "& .text": {
                  textDecoration: validationError.number
                    ? "none"
                    : "line-through",
                },
                "& .dot": {
                  color: error
                    ? "#BA1A1A"
                    : validationError.number
                    ? "#FF6600"
                    : "#979DAC",
                },
                // "& .dot": {
                //   color: validationError.number
                //     ? "#BA1A1A"
                //     : validationError.number
                //     ? "#FF6600"
                //     : "#979DAC",
                // },
              }}
            >
              <span className="dot">●</span>&nbsp;
              <span className="text">At least one number (0-9)</span>
            </FormHelperText>
            <FormHelperText
              sx={{
                color: error ? "#BA1A1A" : "#5C677D",
                fontFamily: poppins.style.fontFamily,
                "& .text": {
                  textDecoration: validationError.alphabet
                    ? "none"
                    : "line-through",
                },
                "& .dot": {
                  color: error
                    ? "#BA1A1A"
                    : validationError.alphabet
                    ? "#FF6600"
                    : "#979DAC",
                },
              }}
            >
              <span className="dot">●</span>&nbsp;
              <span className="text">At least one alphabet</span>
            </FormHelperText>
          </>
        )}
      {!validationError.length &&
        !validationError.number &&
        !validationError.alphabet &&
        password?.length > 0 && (
          <FormHelperText
            sx={{
              mt: 1,
              fontFamily: poppins.style.fontFamily,
              fontSize: "14px",
            }}
          >
            {!isPasswordValid() ? (
              "Password must be 8-30 characters long and contain at least one number."
            ) : (
              <Typography
                variant="span"
                sx={{
                  fontFamily: poppins.style.fontFamily,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <CheckCircleIcon fontSize="small" sx={{ color: "#0B9444" }} />
                Now, that’s a good password!
              </Typography>
            )}
          </FormHelperText>
        )}
      {/* 
      <FormHelperText>
        {error ? (
          helperText
        ) : !isPasswordValid() ? (
          "Password must be 8-30 characters long and contain at least one number."
        ) : (
          <span>Now, that’s a good password!</span>
        )}
      </FormHelperText> */}
      {/* </Box> */}
    </Box>
  );
};

export default PasswordField;

const OutlinedEye = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        fill="#7d8597"
        d="M12 3c5.392 0 9.878 3.88 10.819 9c-.94 5.12-5.427 9-10.819 9s-9.878-3.88-10.818-9C2.122 6.88 6.608 3 12 3m0 16a9.005 9.005 0 0 0 8.778-7a9.005 9.005 0 0 0-17.555 0A9.005 9.005 0 0 0 12 19m0-2.5a4.5 4.5 0 1 1 0-9a4.5 4.5 0 0 1 0 9m0-2a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5"
      ></path>
    </svg>
  );
};

export function EyeOffLine(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        fill="#7d8597"
        d="M17.883 19.297A10.95 10.95 0 0 1 12 21c-5.392 0-9.878-3.88-10.818-9A11 11 0 0 1 4.52 5.935L1.394 2.808l1.414-1.414l19.799 19.798l-1.414 1.415zM5.936 7.35A8.97 8.97 0 0 0 3.223 12a9.005 9.005 0 0 0 13.201 5.838l-2.028-2.028A4.5 4.5 0 0 1 8.19 9.604zm6.978 6.978l-3.242-3.241a2.5 2.5 0 0 0 3.241 3.241m7.893 2.265l-1.431-1.431A8.9 8.9 0 0 0 20.778 12A9.005 9.005 0 0 0 9.552 5.338L7.974 3.76C9.221 3.27 10.58 3 12 3c5.392 0 9.878 3.88 10.819 9a10.95 10.95 0 0 1-2.012 4.593m-9.084-9.084Q11.86 7.5 12 7.5a4.5 4.5 0 0 1 4.492 4.778z"
      ></path>
    </svg>
  );
}
